// Onset strength from raw audio: how much new sound energy arrives in each
// short slice, measured as log-magnitude spectral flux. Drum hits and note
// starts make sharp peaks; the tempo estimator looks for their period.
//
// The rise is averaged within frequency bands and the bands are summed, so a
// kick drum (a few bass bins) counts as much as a hi-hat (hundreds of treble
// bins). A plain sum lets off-beat hi-hats outvote the kick and the lights
// land half a beat late.
//
// Runs inside the AudioWorklet (audio thread) and in Node tests.

export const FRAME_SIZE = 2048;
export const HOP_SIZE = 512;
const BAND_EDGES_HZ = [0, 150, 500, 2000, 8000];

export interface OnsetFrame {
  // Audio-clock seconds at the center of the analyzed frame.
  time: number;
  flux: number;
  rms: number;
}

// Called with each frame's FFT (Hann-windowed; read-only, reused).
export type SpectrumListener = (re: Float32Array, im: Float32Array, time: number) => void;

export class OnsetDetector {
  readonly sampleRate: number;
  private readonly ring = new Float32Array(FRAME_SIZE);
  private readonly window = new Float32Array(FRAME_SIZE);
  private readonly re = new Float32Array(FRAME_SIZE);
  private readonly im = new Float32Array(FRAME_SIZE);
  private readonly prev: Float32Array;
  private readonly mags: Float32Array;
  private readonly maxBin: number;
  private readonly bandOf: Int32Array;
  private readonly bandSize: number[];
  private written = 0;
  private readonly compression: number;
  private readonly onSpectrum: SpectrumListener | undefined;

  // compression: log1p(compression * magnitude). Low values keep quiet
  // background noise in the near-linear range so loud hits dominate; 1 gave
  // the best beat-versus-noise separation on synthetic tests.
  // onSpectrum: also hands each frame's FFT to song analysis (web/analyze.ts),
  // so a song is transformed once for beats and features together.
  constructor(sampleRate: number, compression = 1, onSpectrum?: SpectrumListener) {
    this.compression = compression;
    this.onSpectrum = onSpectrum;
    this.sampleRate = sampleRate;
    for (let i = 0; i < FRAME_SIZE; i++) {
      this.window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (FRAME_SIZE - 1));
    }
    const maxHz = BAND_EDGES_HZ[BAND_EDGES_HZ.length - 1]!;
    this.maxBin = Math.min(FRAME_SIZE / 2 - 1, Math.floor((maxHz * FRAME_SIZE) / sampleRate));
    this.bandOf = new Int32Array(this.maxBin + 1);
    this.bandSize = new Array(BAND_EDGES_HZ.length - 1).fill(0);
    for (let k = 1; k <= this.maxBin; k++) {
      const hz = (k * sampleRate) / FRAME_SIZE;
      let band = 0;
      while (band < BAND_EDGES_HZ.length - 2 && hz >= BAND_EDGES_HZ[band + 1]!) band++;
      this.bandOf[k] = band;
      this.bandSize[band]!++;
    }
    this.prev = new Float32Array(this.maxBin + 2);
    this.mags = new Float32Array(this.maxBin + 2);
  }

  // startTime: audio-clock seconds of samples[0].
  push(samples: Float32Array, startTime: number): OnsetFrame[] {
    const frames: OnsetFrame[] = [];
    for (let i = 0; i < samples.length; i++) {
      this.ring[this.written % FRAME_SIZE] = samples[i]!;
      this.written++;
      if (this.written >= FRAME_SIZE && this.written % HOP_SIZE === 0) {
        const end = startTime + (i + 1) / this.sampleRate;
        frames.push(this.analyze(end - FRAME_SIZE / 2 / this.sampleRate));
      }
    }
    return frames;
  }

  private analyze(time: number): OnsetFrame {
    const { re, im, ring, window, prev } = this;
    const oldest = this.written % FRAME_SIZE;
    let sumSq = 0;
    for (let i = 0; i < FRAME_SIZE; i++) {
      const s = ring[(oldest + i) % FRAME_SIZE]!;
      sumSq += s * s;
      re[i] = s * window[i]!;
      im[i] = 0;
    }
    fft(re, im);
    this.onSpectrum?.(re, im, time);
    const mags = this.mags;
    for (let k = 0; k <= this.maxBin + 1; k++) {
      mags[k] = Math.log1p(this.compression * Math.hypot(re[k]!, im[k]!));
    }
    // Compare each bin with the loudest of its neighbors in the previous frame
    // (as in SuperFlux): sustained notes beating against each other move
    // energy between adjacent bins, which this ignores; real hits rise everywhere.
    const bandRise = new Array<number>(this.bandSize.length).fill(0);
    for (let k = 1; k <= this.maxBin; k++) {
      const rise = mags[k]! - Math.max(prev[k - 1]!, prev[k]!, prev[k + 1]!);
      if (rise > 0) bandRise[this.bandOf[k]!]! += rise;
    }
    let flux = 0;
    for (let b = 0; b < bandRise.length; b++) {
      if (this.bandSize[b]! > 0) flux += bandRise[b]! / this.bandSize[b]!;
    }
    prev.set(mags);
    return { time, flux, rms: Math.sqrt(sumSq / FRAME_SIZE) };
  }
}

// In-place iterative radix-2 FFT. Length must be a power of two.
export function fft(re: Float32Array, im: Float32Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i]!;
      re[i] = re[j]!;
      re[j] = tr;
      const ti = im[i]!;
      im[i] = im[j]!;
      im[j] = ti;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const angle = (-2 * Math.PI) / len;
    const wr = Math.cos(angle);
    const wi = Math.sin(angle);
    const half = len >> 1;
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < half; k++) {
        const a = i + k;
        const b = a + half;
        const tr = re[b]! * cr - im[b]! * ci;
        const ti = re[b]! * ci + im[b]! * cr;
        re[b] = re[a]! - tr;
        im[b] = im[a]! - ti;
        re[a] = re[a]! + tr;
        im[a] = im[a]! + ti;
        const next = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = next;
      }
    }
  }
}
